import type { PrivateEventCrewView } from '../../api/privateEvents';
import type { CrewSlotInput, WorkshopCrewMember } from '../../api/workshops';
import { CrewSlotEditor } from '../dashboards/workshops/detail/CrewSlotEditor';

import NotRecordedPill from './NotRecordedPill';
import { usePrivateEventEditAccess } from './use-private-event-edit-access';
import { useReplacePrivateEventCrew } from './usePrivateEvent';

/** The detail read's crew shape has no separate first/last name — map its
 *  one `name` field into `displayName` so `CrewSlotEditor`'s name resolution
 *  (prefers `displayName`, else first+last) renders it unchanged. */
function toWorkshopCrew(crew: PrivateEventCrewView['crew']): WorkshopCrewMember[] {
  return crew.map((c) => ({
    userId: c.userId,
    firstName: '',
    lastName: '',
    displayName: c.name,
    position: c.position,
  }));
}

/**
 * PrivateEventCrewSection — the detail page's Crew card. Reuses the workshop
 * `CrewSlotEditor` unchanged: assign/remove both go through the existing
 * `PUT /workshops/:id/staff` full-replace, region-scoped to the PRIVATE
 * EVENT's own `regionId` (resolved server-side) rather than a venue lookup,
 * so a venueless client-site event still lists assignable staff.
 *
 * A private event has no publish step and no "workshop started" lock the way a
 * public workshop does, so crew is read-only for a viewer without edit access
 * (the capability plus region breadth) and on a cancelled event, where the
 * crew is released. `viewOnly` is the teacher's view: always read-only, and
 * without the reminder meant for whoever changes the crew.
 */
export default function PrivateEventCrewSection({
  event,
  viewOnly = false,
}: {
  event: PrivateEventCrewView;
  viewOnly?: boolean;
}) {
  const canEdit = usePrivateEventEditAccess(event.regionId);
  const readOnly = viewOnly || !canEdit || event.lifecycle === 'CANCELLED';
  const replace = useReplacePrivateEventCrew(event.id);

  const onSave = async (staff: CrewSlotInput[]) => {
    await replace.mutateAsync(staff);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {event.migrated && event.crew.length === 0 && (
        <span style={{ alignSelf: 'flex-start' }}>
          <NotRecordedPill field="Crew" />
        </span>
      )}
      <CrewSlotEditor
        staff={toWorkshopCrew(event.crew)}
        regionId={event.regionId}
        readOnly={readOnly}
        readOnlyReason={event.lifecycle === 'CANCELLED' ? 'Cancelled' : 'Read-only'}
        // A private event has no draft phase (unlike a workshop) — an empty
        // required teacher slot is urgent from the moment the event exists,
        // so this is always "published" for the editor's own purposes.
        published
        onSave={onSave}
        saving={replace.isPending}
      />
      {!viewOnly && (
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--nb-subtle)' }}>
          Nebari sends no notifications. When crew changes, you tell them — same as workshop
          staffing today.
        </p>
      )}
    </div>
  );
}
